import { useState, useMemo } from 'react';
import { Clock, Star, Search, Filter, X, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { services, addOns } from '../data/mockData';
import { Link, useNavigate } from 'react-router-dom';

const categories = ['Todos', 'Cortes', 'Barba', 'Color', 'Facial', 'Uñas', 'Paquetes'];

const filterOptions = {
  duration: [
    { label: 'Rápido (<30 min)', value: 'fast', max: 30 },
    { label: 'Medio (30-60 min)', value: 'medium', min: 30, max: 60 },
    { label: 'Largo (>60 min)', value: 'long', min: 60 }
  ],
  price: [
    { label: 'Económico (<$200)', value: 'budget', max: 200 },
    { label: 'Medio ($200-$400)', value: 'mid', min: 200, max: 400 },
    { label: 'Premium (>$400)', value: 'premium', min: 400 }
  ]
};

export default function ServicesPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({ duration: null, price: null });
  const [compareList, setCompareList] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      // Category filter
      if (activeCategory !== 'Todos' && service.category !== activeCategory) {
        return false;
      }
      
      // Search filter
      if (searchQuery && !service.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // Duration filter
      if (activeFilters.duration) {
        const filter = filterOptions.duration.find(f => f.value === activeFilters.duration);
        if (filter) {
          if (filter.max && service.duration > filter.max) return false;
          if (filter.min && service.duration < filter.min) return false;
        }
      }
      
      // Price filter
      if (activeFilters.price) {
        const filter = filterOptions.price.find(f => f.value === activeFilters.price);
        if (filter) {
          if (filter.max && service.price > filter.max) return false;
          if (filter.min && service.price < filter.min) return false;
        }
      }
      
      return true;
    });
  }, [activeCategory, searchQuery, activeFilters]);

  const toggleCompare = (service) => {
    if (compareList.find(s => s.id === service.id)) {
      setCompareList(compareList.filter(s => s.id !== service.id));
    } else if (compareList.length < 2) {
      setCompareList([...compareList, service]);
    }
  };

  const clearFilters = () => {
    setActiveFilters({ duration: null, price: null });
    setSearchQuery('');
    setActiveCategory('Todos');
  };

  const hasActiveFilters = activeFilters.duration || activeFilters.price || searchQuery || activeCategory !== 'Todos';

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-12" data-testid="services-page">
      {/* Header */}
      <section className="bg-cream py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl text-graphite mb-4">
            Nuestros Servicios
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Explora todos nuestros servicios y encuentra el perfecto para ti. 
            Compara opciones y agenda en segundos.
          </p>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="sticky top-16 md:top-20 z-40 bg-white border-b border-border/50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar servicios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full border-border"
                data-testid="search-services"
              />
            </div>

            {/* Filter toggle (mobile) */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden rounded-full"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
              {hasActiveFilters && (
                <span className="ml-2 w-2 h-2 bg-copper rounded-full" />
              )}
            </Button>

            {/* Desktop filters */}
            <div className="hidden md:flex gap-2">
              {filterOptions.duration.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setActiveFilters({
                    ...activeFilters,
                    duration: activeFilters.duration === filter.value ? null : filter.value
                  })}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeFilters.duration === filter.value
                      ? 'bg-graphite text-cream'
                      : 'bg-soft-gray/50 hover:bg-soft-gray'
                  }`}
                  data-testid={`filter-${filter.value}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile filters panel */}
          {showFilters && (
            <div className="md:hidden mt-4 p-4 bg-soft-gray/30 rounded-xl space-y-4 animate-fade-in">
              <div>
                <p className="text-sm font-medium mb-2">Duración</p>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.duration.map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setActiveFilters({
                        ...activeFilters,
                        duration: activeFilters.duration === filter.value ? null : filter.value
                      })}
                      className={`px-3 py-1.5 rounded-full text-sm ${
                        activeFilters.duration === filter.value
                          ? 'bg-graphite text-cream'
                          : 'bg-white border border-border'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Precio</p>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.price.map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setActiveFilters({
                        ...activeFilters,
                        price: activeFilters.price === filter.value ? null : filter.value
                      })}
                      className={`px-3 py-1.5 rounded-full text-sm ${
                        activeFilters.price === filter.value
                          ? 'bg-graphite text-cream'
                          : 'bg-white border border-border'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-copper hover:text-copper-dark"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          )}

          {/* Category tabs */}
          <div className="mt-4 overflow-x-auto hide-scrollbar">
            <Tabs value={activeCategory} onValueChange={setActiveCategory}>
              <TabsList className="bg-transparent gap-2 w-max">
                {categories.map((category) => (
                  <TabsTrigger
                    key={category}
                    value={category}
                    data-testid={`tab-${category.toLowerCase()}`}
                    className="px-4 py-2 rounded-full data-[state=active]:bg-graphite data-[state=active]:text-cream"
                  >
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-8 md:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Results count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-muted-foreground text-sm">
              {filteredServices.length} servicios encontrados
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-copper hover:text-copper-dark hidden md:block"
              >
                Limpiar filtros
              </button>
            )}
          </div>

          {filteredServices.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredServices.map((service) => {
                const isInCompare = compareList.find(s => s.id === service.id);
                return (
                  <div
                    key={service.id}
                    className={`comparator-card service-card p-5 rounded-2xl border bg-white ${
                      isInCompare ? 'selected' : 'border-border/50'
                    }`}
                    data-testid={`service-item-${service.id}`}
                  >
                    <div className="flex gap-4 mb-4">
                      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src={service.image}
                          alt={service.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <h3 className="font-medium text-graphite">{service.name}</h3>
                          {service.popular && (
                            <Badge className="badge-limited text-xs">Popular</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {service.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-y border-border/50">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {service.duration} min
                        </span>
                      </div>
                      <span className="font-heading text-xl text-copper">
                        ${service.price}
                      </span>
                    </div>

                    {/* Add-ons */}
                    {service.addOns.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-muted-foreground mb-2">Add-ons disponibles:</p>
                        <div className="flex flex-wrap gap-1">
                          {service.addOns.map((addOnId) => {
                            const addOn = addOns.find(a => a.id === addOnId);
                            return addOn ? (
                              <span key={addOnId} className="px-2 py-0.5 bg-soft-gray/50 rounded text-xs">
                                {addOn.name} +${addOn.price}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleCompare(service)}
                        className={`rounded-full flex-1 ${isInCompare ? 'border-copper text-copper' : ''}`}
                        data-testid={`compare-${service.id}`}
                      >
                        {isInCompare ? (
                          <>
                            <Check className="w-4 h-4 mr-1" /> Comparando
                          </>
                        ) : (
                          'Comparar'
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => navigate('/')}
                        className="rounded-full flex-1 bg-graphite hover:bg-graphite/90 text-cream"
                        data-testid={`book-${service.id}`}
                      >
                        Agendar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground mb-4">No se encontraron servicios</p>
              <Button variant="outline" onClick={clearFilters} className="rounded-full">
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Comparator Panel */}
      {compareList.length > 0 && (
        <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-[500px] bg-white rounded-2xl shadow-premium-hover border border-border p-4 z-50 animate-fade-in-up" data-testid="comparator-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg">Comparar servicios</h3>
            <button
              onClick={() => setCompareList([])}
              className="text-muted-foreground hover:text-graphite"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {compareList.map((service) => (
              <div key={service.id} className="p-3 bg-soft-gray/30 rounded-xl">
                <p className="font-medium text-sm">{service.name}</p>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  <p>Precio: <span className="text-copper font-medium">${service.price}</span></p>
                  <p>Duración: {service.duration} min</p>
                  <p>Categoría: {service.category}</p>
                </div>
              </div>
            ))}
            {compareList.length === 1 && (
              <div className="p-3 border-2 border-dashed border-border rounded-xl flex items-center justify-center text-muted-foreground text-sm">
                Selecciona otro servicio
              </div>
            )}
          </div>

          {compareList.length === 2 && (
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">Recomendación:</p>
              <p className="text-sm">
                {compareList[0].price < compareList[1].price
                  ? `${compareList[0].name} es más económico`
                  : `${compareList[1].name} es más económico`
                }
                {' • '}
                {compareList[0].duration < compareList[1].duration
                  ? `${compareList[0].name} es más rápido`
                  : `${compareList[1].name} es más rápido`
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
